import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Flex, Grid, Link, Spinner } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import TableData from "components/ui/TableData";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import { Tab, Tabs } from "components/ui/Tabs";
import { useToast } from "components/toast";
import { BackArrowIcon } from "components/svg";
import H2 from "components/typography/H2";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import TabSection from "containers/Clients/components/TabSection";

import { getQuoteRequests } from "api/QuoteRequests";
import { getQuotes } from "api/Quotes";
import { getProposalsTable } from "api/Proposals";
import {
  getLead,
  getLeadActivity,
  getLeadLocations,
  getLeadOptions,
  logLeadActivity,
  markLeadLost,
  deleteLead,
  updateLead,
  findLeadContacts,
  getLeadContactSearch,
  toDueDate,
  LEAD_STATUS_LABELS,
  type LeadActivityEvent,
  type LeadStatus,
} from "api/Leads";
import { Pill, NextActionPill, STATUS_STYLE } from "./List";
import SendQuotePanel from "./SendQuotePanel";
import ConvertDialog from "./ConvertDialog";
import NewRfqDialog from "./NewRfqDialog";
import AttachProposalPicker from "./AttachProposalPicker";
import SiblingProposalOffers from "./SiblingProposalOffers";
import OrgContactsMenu from "./OrgContactsMenu";
import FindContactsPanel from "./FindContactsPanel";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";

/**
 * Lead record (LEADS_MODULE_SCOPE.md s.16.2, artboard "Lead").
 *
 * Built in the CLIENT record's visual language so the two read as the
 * same kind of page: back link, H2 title, inline actions, full-width tabs
 * with padded bodies, TabSection headers over white cards, and the
 * Overview tab's two-column grid (details left, activity right). The
 * one thing a lead has that a client does not - the next-action composer
 * - sits at the top of the activity column.
 *
 * RFQs, quotes, proposals and locations are the existing lists filtered
 * to this client id: a lead IS a client row, so nothing is copied.
 */

const columnHelper = createColumnHelper();

const KIND_STYLE: Record<string, { bg: string; fg: string }> = {
  opened: { bg: "#EAF3DE", fg: "#27500A" },
  proposal: { bg: "#FAEEDA", fg: "#633806" },
  note: { bg: "#F1EFE8", fg: "#5f5e5a" },
  call: { bg: "#E6F1FB", fg: "#185FA5" },
  meeting: { bg: "#E6F1FB", fg: "#185FA5" },
  email: { bg: "#EEE9F8", fg: "#4B2E83" },
  quote: { bg: "#FAEEDA", fg: "#633806" },
  rfq: { bg: "#F1EFE8", fg: "#5f5e5a" },
  created: { bg: "#F1EFE8", fg: "#5f5e5a" },
  status: { bg: "#F1EFE8", fg: "#5f5e5a" },
  event: { bg: "#F1EFE8", fg: "#5f5e5a" },
};

const tabBodySx = { paddingTop: "24px", paddingLeft: "32px", paddingRight: "32px", paddingBottom: "24px" };

const inputSx: React.CSSProperties = {
  padding: "8px 10px",
  border: `1px solid ${themevals.colors.inputOutline}`,
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "Poppins",
  width: "100%",
  boxSizing: "border-box",
  color: themevals.colors.black,
  background: "#fff",
};

const LeadRecord = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  // ?send=<quoteId> - arriving from New quote: open that quote's Send panel.
  const [searchParams] = useSearchParams();
  const sendQuoteId = searchParams.get("send");

  const [lead, setLead] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [owners, setOwners] = useState<Array<{ id: string; name: string }>>([]);
  const [events, setEvents] = useState<LeadActivityEvent[]>([]);
  // Long notes (a website message, a call write-up) collapse to a few
  // lines with "Show more"; the full text is always there.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [locations, setLocations] = useState<{ items: any[]; total: number }>({ items: [], total: 0 });
  const [counts, setCounts] = useState<{ rfqs: number | null; quotes: number | null; proposals: number | null }>({ rfqs: null, quotes: null, proposals: null });

  // Composer
  const [kind, setKind] = useState<"note" | "call" | "email" | "meeting">("note");
  const [text, setText] = useState("");
  const [nextNote, setNextNote] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [busy, setBusy] = useState(false);
  // Find contacts (2026-10-15): enrich this contact + list deciders at the org.
  const [finding, setFinding] = useState(false);
  const [findResult, setFindResult] = useState<any>(null);
  // The last saved search, so its people can be reopened later.
  const [lastSearch, setLastSearch] = useState<any>(null);
  useEffect(() => {
    if (!id) return;
    setFindResult(null);
    getLeadContactSearch(id).then(setLastSearch);
  }, [id]);

  const runFindContacts = async () => {
    if (!id || finding) return;
    setFinding(true);
    const startedAt = Date.now();
    const res: any = await findLeadContacts(id);
    if (res?.pending) {
      // The search outlived the request; the server saves it. Wait for it.
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 7500));
        const latest = await getLeadContactSearch(id);
        if (latest?.at && new Date(latest.at).getTime() >= startedAt - 5000) {
          setFinding(false);
          setLastSearch(latest);
          setFindResult(latest);
          load();
          loadActivity();
          return;
        }
      }
      setFinding(false);
      return fail("The contact search is taking longer than usual. Check back in a few minutes.");
    }
    setFinding(false);
    if (res?.error) return fail(res.error);
    if (res.lead) setLead(res.lead);
    setLastSearch(res);
    setFindResult(res);
    loadActivity();
  };

  // Inline editors and dialogs
  const [editingAction, setEditingAction] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState<any>({});
  const [lostOpen, setLostOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [rfqIntent, setRfqIntent] = useState<"rfq" | "quote" | null>(null);
  const [proposalsKey, setProposalsKey] = useState(0);
  const [editingMarkup, setEditingMarkup] = useState(false);
  const [markupDraft, setMarkupDraft] = useState("");
  const { user } = useContext(UserContext) as any;
  const trackedView = useRef(false);

  const fail = (msg: string) => toast.add(msg, "var(--theme-ui-colors-danger)");

  const load = async () => {
    if (!id) return;
    const res: any = await getLead(id);
    if (res?.error) return setError(res.error);
    setLead(res);
    document.title = `${process.env.REACT_APP_NAME} | ${res.name}`;
    // Same page-view tracking the client record does, so lead views sit
    // beside client views in the activity log.
    if (!trackedView.current) {
      trackedView.current = true;
      void trackActivity({
        module: "leads",
        action: "view_lead_detail",
        actionType: "view",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "lead",
        entityId: res.id,
        metadata: { leadName: res.name, leadStatus: res.leadStatus },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }
  };
  const loadActivity = async () => {
    if (!id) return;
    const res: any = await getLeadActivity(id);
    // Never fail silently: an empty feed read as "nothing logged" (2026-10-14).
    if (res?.error) return fail(res.error);
    setEvents(res.items ?? []);
  };

  useEffect(() => {
    trackedView.current = false;
    load();
    loadActivity();
    getLeadOptions().then((o) => setOwners(o.owners));
    if (id) getLeadLocations(id).then(setLocations);
  }, [id]); // eslint-disable-line

  const patch = async (input: any) => {
    if (!id) return;
    const res: any = await updateLead(id, input);
    if (res?.error) return fail(res.error);
    setLead(res);
    loadActivity();
  };

  const saveActivity = async () => {
    if (!id) return;
    if (!text.trim() && !nextNote.trim() && !nextAt) return;
    setBusy(true);
    const res: any = await logLeadActivity(id, {
      kind,
      text: text.trim() || undefined,
      ...(nextNote.trim() || nextAt ? { nextActionAt: toDueDate(nextAt), nextActionNote: nextNote.trim() || null } : {}),
    });
    setBusy(false);
    if (res?.error) return fail(res.error);
    setLead(res);
    setText("");
    setNextNote("");
    setNextAt("");
    setKind("note");
    loadActivity();
  };

  const rfqColumns = useMemo(
    () => [
      columnHelper.accessor("sequence", {
        id: "sequence",
        header: () => <span>RFQ #</span>,
        cell: (info: any) => (
          <Box sx={{ cursor: "pointer", fontWeight: 500, color: "#000" }} onClick={() => navigate(`/quote-requests/${info.row.original.id}`)}>
            {`GMR-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
        ),
        meta: { style: { width: "130px" } },
      }),
      columnHelper.accessor((row: any) => row?.projectName ?? "Instant Estimate", { id: "projectName", header: () => <span>Name</span> }),
      columnHelper.accessor((row: any) => row?.status, {
        id: "status",
        header: () => <span>Status</span>,
        cell: (info: any) => {
          switch (info.getValue()) {
            case 1: return <Badge as="span" variant="warning">New</Badge>;
            case 2: return <Badge as="span" variant="success">Quoted</Badge>;
            case 5: return <Badge as="span" variant="danger">Cancelled</Badge>;
            case 6: return <Badge as="span" variant="orange">Expired</Badge>;
            default: return <Badge as="span" variant="info">Estimated</Badge>;
          }
        },
      }),
      columnHelper.accessor((row: any) => row?.updatedAt, {
        id: "updatedAt",
        header: () => <span>Updated</span>,
        cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      }),
    ],
    [navigate],
  );

  const quoteColumns = useMemo(
    () => [
      columnHelper.accessor("sequence", {
        id: "sequence",
        header: () => <span>Quote #</span>,
        cell: (info: any) => (
          <Box sx={{ cursor: "pointer", fontWeight: 500, color: "#000" }} onClick={() => navigate(`/quotes/${info.row.original.id}`)}>
            {`GMQ-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
        ),
        meta: { style: { width: "130px" } },
      }),
      columnHelper.accessor((row: any) => row?.projectName, { id: "projectName", header: () => <span>Name</span> }),
      columnHelper.accessor((row: any) => row?.status, {
        id: "status",
        header: () => <span>Status</span>,
        cell: (info: any) => {
          switch (info.getValue()) {
            case 1: return <Badge as="span" variant="success">Accepted</Badge>;
            case 2: return <Badge as="span" variant="danger">Denied</Badge>;
            case 3: return <Badge as="span" variant="info">Draft</Badge>;
            case 4: return <Badge as="span" variant="orange">Expired</Badge>;
            case 5: return <Badge as="span" variant="info">Superseded</Badge>;
            case 6: return <Badge as="span" variant="info">Price Locked</Badge>;
            default: return <Badge as="span" variant="warning">New</Badge>;
          }
        },
      }),
      columnHelper.accessor((row: any) => row?.total, {
        id: "total",
        header: () => <span>Total</span>,
        cell: (info: any) => (info.getValue() != null ? Number(info.getValue()).toLocaleString("en-US", { style: "currency", currency: "USD" }) : ""),
      }),
      columnHelper.accessor((row: any) => row?.updatedAt, {
        id: "updatedAt",
        header: () => <span>Updated</span>,
        cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      }),
      columnHelper.accessor((row: any) => row?.id, {
        id: "send",
        header: () => <span>Lead link</span>,
        enableSorting: false,
        cell: (info: any) =>
          lead ? (
            <SendQuotePanel
              leadId={lead.id}
              quoteId={info.row.original.id}
              quoteNumber={`GMQ-${String(info.row.original.sequence).padStart(5, "0")}`}
              defaultTo={lead.contactEmail ?? ""}
              autoOpen={sendQuoteId === info.row.original.id}
              onChange={() => { load(); loadActivity(); }}
            />
          ) : null,
      }),
    ],
    [navigate, lead?.id, lead?.contactEmail, sendQuoteId], // eslint-disable-line
  );

  const proposalColumns = useMemo(
    () => [
      columnHelper.accessor((row: any) => row?.prospectName, {
        id: "prospectName",
        header: () => <span>Proposal</span>,
        cell: (info: any) => (
          <Box sx={{ cursor: "pointer", fontWeight: 500, color: "#000" }} onClick={() => navigate(info.row.original.status === "draft" ? `/proposals/${info.row.original.id}/edit` : `/proposals/${info.row.original.id}`)}>
            {info.getValue()}{info.row.original.version > 1 ? <span style={{ color: themevals.colors.dkgrey, fontWeight: 400 }}> v{info.row.original.version}</span> : null}
          </Box>
        ),
      }),
      columnHelper.accessor((row: any) => row?.status, { id: "status", header: () => <span>Status</span> }),
      columnHelper.accessor((row: any) => row?.nextStep, { id: "nextStep", header: () => <span>Next step</span>, enableSorting: false }),
      columnHelper.accessor((row: any) => row?.viewCount, { id: "viewCount", header: () => <span>Opens</span> }),
      columnHelper.accessor((row: any) => row?.sentAt, {
        id: "sentAt",
        header: () => <span>Sent</span>,
        cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      }),
    ],
    [navigate],
  );

  if (error) {
    return (
      <>
        <Header />
        <Page backAction={<Link variant="back" href="/leads"><BackArrowIcon />Back to Leads</Link>}>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey}>{error}</P>
        </Page>
      </>
    );
  }
  if (!lead) return <Box sx={{ textAlign: "center", paddingY: "1.5rem" }}><Spinner /></Box>;

  const status: LeadStatus = lead.leadStatus;
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.new;
  const states = new Set(locations.items.map((l) => l.address?.state).filter(Boolean));
  const addr = lead.address ?? {};
  const n = (v: number | null) => (v == null ? "" : ` (${v})`);
  const closed = status === "lost" || status === "dormant" || status === "won";
  const ownerName = lead.leadOwner ? `${lead.leadOwner.firstName ?? ""} ${lead.leadOwner.lastName ?? ""}`.trim() : "";

  const tabLinks = [
    { key: "overview", label: "Overview" },
    { key: "rfqs", label: `RFQs${n(counts.rfqs)}` },
    { key: "quotes", label: `Quotes${n(counts.quotes)}` },
    { key: "proposals", label: `Proposals${n(counts.proposals)}` },
    { key: "locations", label: `Locations (${locations.total})` },
  ];

  return (
    <>
      <Header />
      <Page backAction={<Link variant="back" href="/leads"><BackArrowIcon />Back to Leads</Link>}>
        {/* Title row - mirrors the client record: name, then the inline actions. */}
        <Flex sx={{ gap: "16px", alignItems: "center", marginBottom: "4px", flexWrap: "wrap" }}>
          <H2>{lead.name}</H2>
          <Pill bg={s.bg} fg={s.fg}>{LEAD_STATUS_LABELS[status] ?? status}</Pill>
          <Pill bg={themevals.colors.black} fg="#fff">Lead</Pill>
          {lead.possibleDuplicateOf && (
            <span
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/leads/duplicates?focus=${lead.id}`)}
              title={`${lead.possibleDuplicateOf.reason} - ${lead.possibleDuplicateOf.kind} ${lead.possibleDuplicateOf.name}. Click to resolve.`}
            >
              <Pill bg="#FFF6E5" fg="#8A5A00">
                Possible duplicate of {lead.possibleDuplicateOf.kind} {lead.possibleDuplicateOf.name}
              </Pill>
            </span>
          )}
          {Array.isArray(lead.sameOrg) && lead.sameOrg.length > 0 && (
            <OrgContactsMenu orgName={lead.name} current={{ id: lead.id, contactName: lead.contactName ?? null }} others={lead.sameOrg} />
          )}
          <Box sx={{ flex: 1 }} />
          <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
            {!closed && (
              <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setLostOpen(true)}>Mark lost</BasicButton>
            )}
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setDeleteOpen(true)}>Delete</BasicButton>
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate(`/leads/new?cloneFrom=${lead.id}`)}>Clone for another contact</BasicButton>
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setRfqIntent("rfq")}>New RFQ</BasicButton>
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setRfqIntent("quote")}>New quote</BasicButton>
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate(`/proposals/new?leadId=${lead.id}`)}>New proposal</BasicButton>
            <BasicButton type="button" keepText onClick={() => setConvertOpen(true)}>Convert to client</BasicButton>
          </Flex>
        </Flex>
        <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} marginBottom={"14px"} color={themevals.colors.dkgrey}>
          {lead.contactName && (
            <Box as="span" sx={{ display: "block", fontSize: "15px", fontWeight: 500, color: "#1a2332", marginBottom: "4px" }}>
              {[lead.contactName, lead.contactTitle].filter(Boolean).join(" \u00b7 ")}
            </Box>
          )}
          {[
            ownerName ? `Owner ${ownerName}` : "No owner",
            `Source: ${(lead.leadSource ?? "\u2014").replace(/_/g, " ")}`,
            `Created ${moment(lead.createdAt).format("MMM D, YYYY")}`,
            `${locations.total} site${locations.total === 1 ? "" : "s"}${states.size ? ` in ${states.size} state${states.size === 1 ? "" : "s"}` : ""}`,
          ].join(" \u00b7 ")}
        </Subtext>

        {/* Keyed on ?send so arriving from New quote (same page, new query)
            remounts the tabs on Quotes instead of keeping Overview. */}
        <Tabs key={sendQuoteId ?? "tabs"} links={tabLinks}>
          {/* ------------------------------------------------ Overview */}
          <Tab itemKey="overview">
            <Box sx={tabBodySx}>
              <Grid gap={"28px"} columns={[1, "340px 1fr"]}>
                {/* Left column - what the lead is */}
                <Box>
                  <SectionLabel action={
                    <TextButton onClick={() => { setNextAt(lead.nextActionAt ? moment(lead.nextActionAt).format("YYYY-MM-DD") : ""); setNextNote(lead.nextActionNote ?? ""); setEditingAction((v) => !v); }}>
                      {editingAction ? "Cancel" : "Edit"}
                    </TextButton>
                  }>Next action</SectionLabel>
                  <Card>
                    <Box sx={{ padding: "16px 20px" }}>
                      {!editingAction ? (
                        <>
                          <NextActionPill at={lead.nextActionAt} />
                          <P fontFamily="Poppins" fontSize={"13px"} fontWeight={500} marginTop={"8px"} marginBottom={0} color={themevals.colors.black}>
                            {lead.nextActionNote || <Span color={themevals.colors.dkgrey} fontWeight={400}>Nothing set</Span>}
                          </P>
                          {lead.nextActionAt && (
                            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} color={themevals.colors.dkgrey}>
                              {lead.lastUpdateBy ? `Set by ${lead.lastUpdateBy.firstName ?? ""} ${lead.lastUpdateBy.lastName ?? ""} \u00b7 ` : ""}due {moment(lead.nextActionAt).format("MMM D")}
                            </Subtext>
                          )}
                        </>
                      ) : (
                        <Box>
                          <input style={{ ...inputSx, marginBottom: 8 }} value={nextNote} onChange={(e) => setNextNote(e.target.value)} placeholder="What to do next" />
                          <input style={{ ...inputSx, marginBottom: 10 }} type="date" value={nextAt} onChange={(e) => setNextAt(e.target.value)} />
                          <Flex sx={{ gap: "8px" }}>
                            <BasicButton type="button" onClick={async () => { await patch({ nextActionAt: toDueDate(nextAt), nextActionNote: nextNote.trim() || null }); setEditingAction(false); setNextAt(""); setNextNote(""); }}>Save</BasicButton>
                            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => patch({ nextActionAt: null, nextActionNote: null }).then(() => setEditingAction(false))}>Clear</BasicButton>
                          </Flex>
                        </Box>
                      )}
                    </Box>
                  </Card>

                  <Box sx={{ marginTop: "28px" }}>
                    <SectionLabel action={
                      <Flex sx={{ gap: "14px", alignItems: "center" }}>
                        <TextButton onClick={runFindContacts}>
                          {finding ? "Searching\u2026" : "Find contacts"}
                        </TextButton>
                        <TextButton onClick={() => { setContactDraft({ contactName: lead.contactName ?? "", contactTitle: lead.contactTitle ?? "", contactEmail: lead.contactEmail ?? "", phone: lead.phone ?? "", website: lead.website ?? "", line1: addr.line1 ?? "", city: addr.city ?? "", state: addr.state ?? "", postalCode: addr.postalCode ?? "" }); setEditingContact((v) => !v); }}>
                          {editingContact ? "Cancel" : "Edit"}
                        </TextButton>
                      </Flex>
                    }>Contact</SectionLabel>
                    <Card>
                      <Box sx={{ padding: "16px 20px" }}>
                        {!editingContact ? (
                          <Flex sx={{ alignItems: "flex-start", gap: "14px" }}>
                            <Flex sx={{ width: "32px", height: "32px", borderRadius: "50%", background: "#F1EFE8", color: themevals.colors.dkgrey, alignItems: "center", justifyContent: "center", fontFamily: "Poppins", fontSize: "11px", fontWeight: 600, flexShrink: 0 }}>
                              {initials(lead.contactName)}
                            </Flex>
                            <Box sx={{ minWidth: 0, fontFamily: "Poppins", fontSize: "13px", lineHeight: 1.6, color: themevals.colors.black }}>
                              <Box sx={{ fontWeight: 600 }}>{lead.contactName || <Span color={themevals.colors.dkgrey} fontWeight={400}>No contact yet</Span>}</Box>
                              {lead.contactTitle && <Box sx={{ color: themevals.colors.dkgrey, fontSize: "12px" }}>{lead.contactTitle}</Box>}
                              {lead.contactEmail && <Box><a href={`mailto:${lead.contactEmail}`} style={{ color: "#185FA5" }}>{lead.contactEmail}</a></Box>}
                              {lead.phone && <Box>{lead.phone}</Box>}
                              {lead.website && <Box><a href={/^https?:/.test(lead.website) ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" style={{ color: "#185FA5" }}>{lead.website.replace(/^https?:\/\//, "")}</a></Box>}
                              {(addr.line1 || addr.city) && (
                                <Box sx={{ color: themevals.colors.dkgrey, fontSize: "12px", marginTop: "6px" }}>
                                  {addr.line1 && <div>{addr.line1}</div>}
                                  <div>{[addr.city, [addr.state, addr.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
                                </Box>
                              )}
                            </Box>
                          </Flex>
                        ) : (
                          <Box>
                            {(["contactName", "contactTitle", "contactEmail", "phone", "website", "line1", "city", "state", "postalCode"] as const).map((k) => (
                              <input key={k} style={{ ...inputSx, marginBottom: 8 }} placeholder={{ contactName: "Name", contactTitle: "Title", contactEmail: "Email", phone: "Phone", website: "Website", line1: "Street", city: "City", state: "State", postalCode: "ZIP" }[k]} value={contactDraft[k] ?? ""} onChange={(e) => setContactDraft({ ...contactDraft, [k]: e.target.value })} />
                            ))}
                            <BasicButton type="button" onClick={async () => {
                              const { line1, city, state, postalCode, ...rest } = contactDraft;
                              await patch({ ...rest, address: line1 || city || state || postalCode ? { ...addr, line1, city, state, postalCode } : null });
                              setEditingContact(false);
                            }}>Save</BasicButton>
                          </Box>
                        )}
                      </Box>
                    </Card>
                    {!finding && !findResult && lastSearch && (lastSearch.people?.length ?? 0) > 0 && (
                      <Box sx={{ marginTop: "8px", fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>
                        Last contact search {moment(lastSearch.at).fromNow()}: {lastSearch.people.length} {lastSearch.people.length === 1 ? "person" : "people"} found.{" "}
                        <TextButton onClick={() => setFindResult(lastSearch)}>View</TextButton>
                      </Box>
                    )}
                    {finding && !findResult && (
                      <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"8px"} color={themevals.colors.dkgrey}>
                        Looking up this contact and the decision-makers at {lead.name}. This can take up to a minute.
                      </Subtext>
                    )}
                    {findResult && (
                      <FindContactsPanel
                        lead={lead}
                        result={findResult}
                        onClose={() => setFindResult(null)}
                        onAdded={() => load()}
                        onMakeMain={async (c: any) => {
                          await patch({ contactName: c.name, contactTitle: c.title ?? null, contactEmail: c.email ?? null, phone: c.phone ?? lead.phone ?? null });
                          setFindResult((r: any) => (r ? { ...r, people: r.people.filter((p: any) => p.name !== c.name) } : r));
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={{ marginTop: "28px" }}>
                    <SectionLabel>Details</SectionLabel>
                    <Card>
                      <Box sx={{ padding: "16px 20px" }}>
                        <FieldRow label="Status">
                          <select style={inputSx} value={status} onChange={(e) => patch({ leadStatus: e.target.value })}>
                            {(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((k) => (
                              <option key={k} value={k} disabled={k === "won" && status !== "won"}>{LEAD_STATUS_LABELS[k]}</option>
                            ))}
                          </select>
                          {status === "lost" && lead.lostReason && (
                            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} color={themevals.colors.dkgrey}>Reason: {lead.lostReason}</Subtext>
                          )}
                        </FieldRow>
                        <FieldRow label="Owner">
                          <select style={inputSx} value={lead.leadOwner?.id ?? ""} onChange={(e) => patch({ leadOwnerId: e.target.value || null })}>
                            <option value="">Nobody</option>
                            {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </FieldRow>
                        <FieldRow label="Lead pricing" isLast>
                          {!editingMarkup ? (
                            <Flex sx={{ alignItems: "baseline", justifyContent: "space-between", gap: "10px" }}>
                              <Box>
                                <Span fontFamily="Poppins" fontSize={"13px"} fontWeight={600} color={themevals.colors.black}>
                                  Quotes priced at +{lead.effectiveMarkupPercent ?? lead.companyMarkupPercent ?? 10}%
                                </Span>
                                <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"2px"} color={themevals.colors.dkgrey}>
                                  {lead.leadMarkupPercent != null
                                    ? `Set for this lead ${"\u00b7"} company default is ${lead.companyMarkupPercent ?? 10}%`
                                    : "Company default"}
                                </Subtext>
                              </Box>
                              <TextButton onClick={() => { setMarkupDraft(String(lead.effectiveMarkupPercent ?? lead.companyMarkupPercent ?? 10)); setEditingMarkup(true); }}>Change</TextButton>
                            </Flex>
                          ) : (
                            <Box>
                              <Flex sx={{ gap: "8px", alignItems: "center", marginBottom: "10px" }}>
                                <input
                                  style={{ ...inputSx, width: 90 }}
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={0.5}
                                  value={markupDraft}
                                  onChange={(e) => setMarkupDraft(e.target.value)}
                                  autoFocus
                                />
                                <Span fontFamily="Poppins" fontSize={"13px"} color={themevals.colors.black}>% on this lead&rsquo;s quotes</Span>
                              </Flex>
                              <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
                                <BasicButton
                                  type="button"
                                  disabled={markupDraft.trim() === "" || !(Number(markupDraft) >= 0) || Number(markupDraft) > 100}
                                  onClick={async () => { await patch({ leadMarkupPercent: Number(markupDraft) }); setEditingMarkup(false); }}
                                >
                                  Save
                                </BasicButton>
                                {lead.leadMarkupPercent != null && (
                                  <BasicButton scheme="secondaryOutline" type="button" keepText onClick={async () => { await patch({ leadMarkupPercent: null }); setEditingMarkup(false); }}>
                                    Use company default ({lead.companyMarkupPercent ?? 10}%)
                                  </BasicButton>
                                )}
                                <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setEditingMarkup(false)}>Cancel</BasicButton>
                              </Flex>
                              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"8px"} color={themevals.colors.dkgrey}>
                                Applies to quotes created from now on. Existing quotes keep the markup they were priced at.
                              </Subtext>
                            </Box>
                          )}
                        </FieldRow>
                      </Box>
                    </Card>
                  </Box>
                </Box>

                {/* Right column - what has happened, and what to do next */}
                <Box>
                  <SectionLabel subtitle="notes, calls, proposals, quotes">Activity</SectionLabel>
                  <Card>
                    <Box sx={{ padding: "16px 20px", borderBottom: "1px solid", borderColor: "inputOutline", background: "#FAFAF8" }}>
                      <Flex sx={{ gap: "6px", marginBottom: "10px", flexWrap: "wrap" }}>
                        {(["note", "call", "email", "meeting"] as const).map((k) => (
                          <Chip key={k} active={kind === k} onClick={() => setKind(k)}>
                            {{ note: "Note", call: "Call", email: "Email sent", meeting: "Meeting" }[k]}
                          </Chip>
                        ))}
                      </Flex>
                      <textarea
                        style={{ ...inputSx, minHeight: 72, marginBottom: 10, resize: "vertical" }}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={{ note: "What happened, what they said, what they want.", call: "Who you spoke to, how long, what was agreed.", email: "What you sent and what you asked for.", meeting: "Who was there and what was decided." }[kind]}
                      />
                      <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                        <input style={{ ...inputSx, flex: "2 1 220px" }} value={nextNote} onChange={(e) => setNextNote(e.target.value)} placeholder="Next action (optional)" />
                        <input style={{ ...inputSx, flex: "0 1 160px" }} type="date" value={nextAt} onChange={(e) => setNextAt(e.target.value)} />
                        <BasicButton type="button" onClick={saveActivity} disabled={busy || (!text.trim() && !nextNote.trim() && !nextAt)} $submitting={busy}>Save</BasicButton>
                      </Flex>
                    </Box>

                    <Box sx={{ maxHeight: "620px", overflowY: "auto" }}>
                      {events.length === 0 && (
                        <Box sx={{ padding: "24px" }}>
                          <P color={themevals.colors.dkgrey} fontSize={"13px"} marginTop={0} marginBottom={0}>Nothing yet. The first note, call or proposal shows up here.</P>
                        </Box>
                      )}
                      {events.map((e, i) => {
                        const ks = KIND_STYLE[e.kind] ?? KIND_STYLE.event;
                        const href = e.refType === "proposal" ? `/proposals/${e.refId}` : e.refType === "quote" ? `/quotes/${e.refId}` : e.refType === "rfq" ? `/quote-requests/${e.refId}` : null;
                        return (
                          <Box
                            key={`${e.at}-${i}`}
                            onClick={href ? () => navigate(href) : undefined}
                            sx={{ padding: "14px 20px", borderBottom: i === events.length - 1 ? "none" : "1px solid", borderColor: "inputOutline", cursor: href ? "pointer" : "default", "&:hover": href ? { background: "#FAFAF8" } : undefined }}
                          >
                            <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                              <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: ks.fg, flexShrink: 0 }} />
                              <span title={moment(e.at).format("MMM D, YYYY h:mm A")}>
                                <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
                                  {relativeTime(e.at)}
                                </Subtext>
                              </span>
                              <Pill bg={ks.bg} fg={ks.fg}>{e.kind}</Pill>
                            </Flex>
                            <Box sx={{ position: "relative" }}>
                              <Box
                                sx={{
                                  fontFamily: "Poppins",
                                  fontSize: "13px",
                                  color: themevals.colors.black,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  ...(!expanded[`${e.at}-${i}`] && (e.text ?? "").length > 320 ? { maxHeight: "120px", overflow: "hidden" } : {}),
                                }}
                              >
                                {e.text}
                              </Box>
                              {(e.text ?? "").length > 320 && (
                                <button
                                  type="button"
                                  onClick={(ev) => { ev.stopPropagation(); setExpanded((x) => ({ ...x, [`${e.at}-${i}`]: !x[`${e.at}-${i}`] })); }}
                                  style={{ background: "none", border: "none", padding: 0, marginTop: 4, color: "#185FA5", cursor: "pointer", fontFamily: "Poppins", fontSize: 12, fontWeight: 500 }}
                                >
                                  {expanded[`${e.at}-${i}`] ? "Show less" : "Show more"}
                                </button>
                              )}
                            </Box>
                            {e.actor && (
                              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>{e.actor}</Subtext>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Card>
                </Box>
              </Grid>
            </Box>
          </Tab>

          {/* ------------------------------------------------ RFQs */}
          <Tab itemKey="rfqs">
            <Box sx={tabBodySx}>
              <TabSection title="RFQs" subtitle="requests for this lead, most recent first" action={<TextButton onClick={() => setRfqIntent("rfq")}>New RFQ</TextButton>}>
                <TableData api={getQuoteRequests} columns={rfqColumns} defaultSortBy="updatedAt" defaultSortDir="desc" defaultFilters={{ clientId: lead.id }} nothingFoundText="No RFQs for this lead yet." returnData={(res: any) => setCounts((c) => ({ ...c, rfqs: typeof res?.total === "number" ? res.total : c.rfqs }))} stateKey={`lead-${lead.id}-rfqs`} />
              </TabSection>
            </Box>
          </Tab>

          {/* ------------------------------------------------ Quotes */}
          <Tab itemKey="quotes">
            <Box sx={tabBodySx}>
              <TabSection title="Quotes" subtitle="quotes for this lead and the public link each one went out on" action={<TextButton onClick={() => setRfqIntent("quote")}>New quote</TextButton>}>
                <TableData api={getQuotes} columns={quoteColumns} defaultSortBy="updatedAt" defaultSortDir="desc" defaultFilters={{ clientId: lead.id }} nothingFoundText="No quotes for this lead yet." returnData={(res: any) => setCounts((c) => ({ ...c, quotes: typeof res?.total === "number" ? res.total : c.quotes }))} stateKey={`lead-${lead.id}-quotes`} />
              </TabSection>
            </Box>
          </Tab>

          {/* ------------------------------------------------ Proposals */}
          <Tab itemKey="proposals">
            <Box sx={tabBodySx}>
              <TabSection title="Proposals" subtitle="proposals attached to this lead" action={<TextButton onClick={() => navigate(`/proposals/new?leadId=${lead.id}`)}>New proposal</TextButton>}>
                <Box sx={{ padding: "12px 20px 0" }}>
                  <SiblingProposalOffers leadId={lead.id} contactName={lead.contactName ?? null} contactEmail={lead.contactEmail ?? null} onSent={() => { setProposalsKey((k) => k + 1); loadActivity(); load(); }} />
                  <AttachProposalPicker leadId={lead.id} leadName={lead.name} onAttached={() => { setProposalsKey((k) => k + 1); loadActivity(); load(); }} />
                </Box>
                <TableData key={`proposals-${proposalsKey}`} api={getProposalsTable} columns={proposalColumns} defaultSortBy="updatedAt" defaultSortDir="desc" defaultFilters={{ clientId: lead.id }} nothingFoundText="No proposals for this lead yet." returnData={(res: any) => setCounts((c) => ({ ...c, proposals: typeof res?.total === "number" ? res.total : c.proposals }))} stateKey={`lead-${lead.id}-proposals`} />
              </TabSection>
            </Box>
          </Tab>

          {/* ------------------------------------------------ Locations */}
          <Tab itemKey="locations">
            <Box sx={tabBodySx}>
              <TabSection title="Locations" subtitle="sites on this lead">
                {locations.items.length === 0 && (
                  <Box sx={{ padding: "20px" }}>
                    <P color={themevals.colors.dkgrey} fontSize={"13px"} marginTop={0} marginBottom={0}>No sites yet. Sites arrive with an RFQ or a proposal.</P>
                  </Box>
                )}
                {locations.items.map((l, i) => (
                  <Flex
                    key={l.profileId}
                    sx={{ gap: "14px", padding: "14px 20px", borderBottom: i === locations.items.length - 1 ? "none" : "1px solid", borderColor: "inputOutline", alignItems: "center", cursor: "pointer", "&:hover": { background: "#FAFAF8" } }}
                    onClick={() => navigate(`/locations/${l.locationId}`)}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Span fontFamily={"Poppins"} fontWeight={600} fontSize={"13px"} color={themevals.colors.black}>{l.name}</Span>
                      <Box>
                        <Span fontFamily={"Poppins"} fontSize={"11px"} color={themevals.colors.dkgrey}>
                          {[l.address?.line1, l.address?.city, l.address?.state].filter(Boolean).join(", ")}
                        </Span>
                      </Box>
                    </Box>
                    <Span fontFamily={"Poppins"} fontSize={"11px"} color={themevals.colors.dkgrey}>{l.siteContactName ?? ""}</Span>
                  </Flex>
                ))}
              </TabSection>
            </Box>
          </Tab>
        </Tabs>

        {lostOpen && (
          <BasicModal
            title={`Mark ${lead.name} lost`}
            cancel_click={() => setLostOpen(false)}
            cancel_text="Cancel"
            confirm_text="Mark lost"
            confirm_click={async () => { if (!id) return; const res: any = await markLeadLost(id, lostReason.trim() || null); if (res?.error) return fail(res.error); setLead(res); setLostOpen(false); loadActivity(); }}
          >
            <P fontFamily="Poppins" fontSize={"13px"} marginTop={0} marginBottom={"10px"} color={themevals.colors.black}>The lead stays on record with its history. Nothing is deleted; the next action is cleared.</P>
            <input style={inputSx} value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Why (optional) - price, timing, went elsewhere" autoFocus />
          </BasicModal>
        )}
        {deleteOpen && (
          <BasicModal
            title={`Delete ${lead.name}?`}
            cancel_click={() => setDeleteOpen(false)}
            cancel_text="Cancel"
            confirm_text={deleting ? "Deleting…" : "Delete lead"}
            submitting={deleting}
            confirm_click={async () => {
              if (!id || deleting) return;
              setDeleting(true);
              const res: any = await deleteLead(id);
              setDeleting(false);
              if (res?.error) return fail(res.error);
              toast.add(`${lead.name} deleted.`, "var(--theme-ui-colors-success)");
              navigate("/leads");
            }}
          >
            <P fontFamily="Poppins" fontSize={"13px"} marginTop={0} marginBottom={"8px"} color={themevals.colors.black}>
              It leaves the leads list and search, and any public quote links for it stop working. RFQs, quotes and proposals stay in history.
            </P>
            <P fontFamily="Poppins" fontSize={"12px"} marginTop={0} marginBottom={0} color={themevals.colors.dkgrey}>
              For a prospect that simply went elsewhere, Mark lost keeps it on record for reporting. A lead with a project or an invoice cannot be deleted.
            </P>
          </BasicModal>
        )}
        {rfqIntent && (
          <NewRfqDialog leadId={lead.id} leadName={lead.name} contactName={lead.contactName ?? null} contactEmail={lead.contactEmail ?? null} intent={rfqIntent} onClose={() => setRfqIntent(null)} />
        )}
        {convertOpen && (
          <ConvertDialog leadId={lead.id} leadName={lead.name} convertedBy={[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "you"} onClose={() => setConvertOpen(false)} />
        )}
      </Page>
    </>
  );
};

/* ---- the same small pieces the client Overview tab uses ---------------- */

function SectionLabel({ children, subtitle, action }: { children: React.ReactNode; subtitle?: string; action?: React.ReactNode }) {
  return (
    <Flex sx={{ alignItems: "baseline", gap: "10px", marginBottom: "12px" }}>
      <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"} color={themevals.colors.black}>{children}</H5>
      {subtitle && <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>{subtitle}</Subtext>}
      {action && <Box sx={{ marginLeft: "auto" }}>{action}</Box>}
    </Flex>
  );
}

function Card({ children, sx }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box sx={{ background: "white", border: "1px solid", borderColor: "inputOutline", borderRadius: "8px", overflow: "hidden", boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)", ...(sx ?? {}) }}>
      {children}
    </Box>
  );
}

function FieldRow({ label, children, isLast }: { label: string; children: React.ReactNode; isLast?: boolean }) {
  return (
    <Box sx={{ marginBottom: isLast ? 0 : "16px" }}>
      <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginBottom={"4px"} color={themevals.colors.dkgrey}>{label}</Subtext>
      {children}
    </Box>
  );
}

function TextButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "Poppins", fontSize: 12, fontWeight: 500, color: "#185FA5" }}>
      {children}
    </button>
  );
}

function Chip({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "4px 12px",
        borderRadius: "14px",
        border: `1px solid ${active ? themevals.colors.black : themevals.colors.inputOutline}`,
        background: active ? themevals.colors.black : "#fff",
        color: active ? "#fff" : themevals.colors.black,
        fontFamily: "Poppins",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function relativeTime(at: string): string {
  const m = moment(at);
  if (m.isSame(moment(), "day")) return `Today \u00b7 ${m.format("h:mm A")}`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return `Yesterday \u00b7 ${m.format("h:mm A")}`;
  const days = moment().diff(m, "days");
  if (days < 7) return `${days} days ago`;
  return m.format("MMM D, YYYY");
}

function initials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Remount the whole record when the URL's lead id changes (2026-09-28).
 * React Router reuses the component between /leads/A and /leads/B - e.g.
 * the "N contacts at <org>" menu - so tables that read `defaultFilters`
 * once on mount kept showing A's proposals/RFQs/quotes until a refresh,
 * and counts, drafts and open editors carried over. A key on the id
 * resets all of it.
 */
const LeadRecordRoute = () => {
  const { id } = useParams();
  return <LeadRecord key={id} />;
};

export default LeadRecordRoute;
